import { StoreExt } from '@utils/reactExt'
import { action, observable, runInAction } from 'mobx'
import { Schema$CheerForTeam, Schema$SportTalkListItem } from '@interface/cheer-for-team'
import _ from 'lodash'
import { COMMUNICATION_BLOCK_TYPE } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import { SPORT_TALK_ITEM_TYPE } from '@v2/constants/sport-issue'
import { matchupStore } from '@store/index'
import { Mining } from '@game/util'
import { BET_TYPE_ON } from 'adv-protocol/src/GameCommon'
import { Schema$SimpleMatchTrack } from '@interface/matchup'
import { SimpleMatchTrack } from '@store/matchupStore'
import { isMobile } from 'react-device-detect'

interface IFetchSportTalkParams {
  targetList: 'sportTalkDetailList' | 'sportTalkPopupList'
  targetPageNum: 'fetchCheerForTeamPageNum' | 'fetchSportTalkPopupPageNum'
  targetHasMore: 'hasMoreCheerForTeam' | 'hasMoreSportTalkPopupList'
  resetFunction: 'resetFetchCheerForTeamPagination' | 'resetFetchSportTalkPopupPagination'
}

export class CheerForTeamStore extends StoreExt {
  @observable
  sportTalkDetailList: Schema$SportTalkListItem[]

  hasMoreCheerForTeam: boolean

  fetchCheerForTeamPageNum: number

  @observable
  sportTalkPopupList: Schema$SportTalkListItem[]

  hasMoreSportTalkPopupList: boolean

  fetchSportTalkPopupPageNum: number

  @observable
  myCheerForTeams: Schema$CheerForTeam[]

  hasMoreMyCheerForTeams: boolean

  fetchMyCheerForTeamPageNum: number

  isFetchCheerForTeamLoading: boolean

  isFetchSimpleMatchInfo: boolean

  @observable
  effectEnabled: boolean = StorageUtils.getCheerForTeamEffectEnabled()

  @observable
  sportTalkPopupMatchTrack: SimpleMatchTrack

  documentVisibleChangeHandler = (isHidden: boolean) => {
    if (!isMobile) {
      return
    }

    if (isHidden) {
      return
    }

    runInAction(() => {
      this.isFetchSimpleMatchInfo = false
      this.isFetchCheerForTeamLoading = false
    })
  }

  @action
  resetFetchCheerForTeamPagination() {
    this.hasMoreCheerForTeam = false
    this.fetchCheerForTeamPageNum = 0
  }

  @action
  resetFetchSportTalkPopupPagination() {
    this.hasMoreSportTalkPopupList = false
    this.fetchSportTalkPopupPageNum = 0
  }

  @action
  resetMyFetchCheerForTeam() {
    this.myCheerForTeams = []
    this.hasMoreMyCheerForTeams = false
    this.fetchMyCheerForTeamPageNum = 0
  }

  async fetchSportTalks(params: {
    matchId: number
    reset?: boolean
    timelineDisabled?: boolean
    targetParams: IFetchSportTalkParams
  }) {
    try {
      const {
        matchId,
        targetParams,
        reset = false,
        timelineDisabled = false
      } = params
      const { targetList, targetPageNum, targetHasMore, resetFunction } = targetParams

      let lastCheerForTeamTime

      if (reset) {
        this[resetFunction]()
      } else {
        lastCheerForTeamTime = _.chain(
          this[targetList]
        ).filter({
          sportTalkItemType: SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM
        }).last().value()?.createdAt
      }

      const result = await this.api.cheerForTeam.fetchSportTalkList({
        matchId,
        pageNum: this[targetPageNum],
        lastCheerForTeamTime,
        timelineDisabled,
      })

      runInAction(() => {
        if (reset) {
          this[targetList] = _.concat([], result.items)
        } else {
          this[targetList] = this[targetList].concat(result.items)
        }
        this[targetHasMore] = result.hasMore
      })

      if (result.hasMore) {
        this[targetPageNum] += 1
      }
    } catch (err) {
      console.error(err)
    }
  }

  async fetchSportTalkListInMatchDetail(matchId: number, reset = false, timelineDisabled = false): Promise<void> {
    await this.fetchSportTalks({
      matchId,
      reset,
      timelineDisabled,
      targetParams: {
        targetList: 'sportTalkDetailList',
        targetPageNum: 'fetchCheerForTeamPageNum',
        targetHasMore: 'hasMoreCheerForTeam',
        resetFunction: 'resetFetchCheerForTeamPagination',
      },
    })
  }

  async fetchSportTalkListInPopup(matchId: number, reset = false, timelineDisabled = false) {
    await this.fetchSportTalks({
      matchId,
      reset,
      timelineDisabled,
      targetParams: {
        targetList: 'sportTalkPopupList',
        targetPageNum: 'fetchSportTalkPopupPageNum',
        targetHasMore: 'hasMoreSportTalkPopupList',
        resetFunction: 'resetFetchSportTalkPopupPagination',
      },
    })
  }

  @action
  setAttributesByCheerForTeamId(params: { cheerForTeamId: number, reported?: boolean, }): void {
    const { cheerForTeamId, reported } = params
    const origin = _.find(this.sportTalkDetailList, sportTalk => sportTalk.cheerForTeam?.id === cheerForTeamId)
    if (origin && !_.isNil(reported)) {
      origin.cheerForTeam.reported = reported
    }
  }

  @action
  setCheerForTeamsUserBlock(userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]): void {
    const isUserBlock = !_.isEmpty(blockTypes)
    const isCheerForTeamBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.CHAT_AND_SPORT_TALK)
    const sportTalks = _.filter(this.sportTalkDetailList, { sportTalkItemType: SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM })

    _.forEach(sportTalks, sportTalk => {
      const { cheerForTeam } = sportTalk
      if (cheerForTeam.owner.userPromiseId === userPromiseId) {
        cheerForTeam.isBlocked = isUserBlock
        cheerForTeam.owner.isBlocked = isCheerForTeamBlock
      }
    })

    const matchIds = _.map(sportTalks, talk => talk.cheerForTeam?.matchId)
    matchupStore.setSportTalkUserBlock(matchIds, userPromiseId, isCheerForTeamBlock)
  }

  async deleteCheerForTeam(cheerForTeamId: number) {
    try {
      await this.api.cheerForTeam.deleteCheerForTeam({ cheerForTeamId })
    } catch (err) {
      console.error(err)
    }
  }

  async fetchMyCheerForTeams(baseMonth?: string, reset = false): Promise<void> {
    this.isFetchCheerForTeamLoading = true
    try {
      if (reset) {
        this.resetMyFetchCheerForTeam()
      }

      const params = {
        pageNum: this.fetchMyCheerForTeamPageNum,
      }

      if (!_.isNil(baseMonth)) {
        params['baseMonth'] = baseMonth
      }

      const result = await this.api.my.fetchMyCheerForTeamList(params)

      runInAction(() => {
        this.myCheerForTeams = this.myCheerForTeams.concat(result.items)
        this.hasMoreMyCheerForTeams = result.hasMore
      })

      if (result.hasMore) {
        this.fetchMyCheerForTeamPageNum += 1
      }
    } catch (err) {
      console.error(err)
    }
    this.isFetchCheerForTeamLoading = false
  }

  @action
  toggleEffectEnabled(): void {
    StorageUtils.saveCheerForTeamEffectEnabled(!this.effectEnabled)
    this.effectEnabled = !this.effectEnabled
  }

  async fetchSportTalkDetailPackage(params: {
    matchId: number
    sportId: number
  }) {
    const result = await this.api.cheerForTeam.sportTalkMatchDetailPackage(params)
    Mining.sortOutcome(BET_TYPE_ON.UNIFIED_MATCH, result.outcomes)
    return result
  }

  @action
  async fetchSportTalkSimpleMatchInfoTrack(matchId: number) {
    if (this.isFetchSimpleMatchInfo) {
      return
    }

    this.isFetchSimpleMatchInfo = true
    const result = await this.api.cheerForTeam.fetchSportTalkSimpleMatchInfoTrack({ matchId })
    this.setSimpleMatchInfo(result.simpleMatchInfo)

    this.isFetchSimpleMatchInfo = false
  }

  @action
  setSimpleMatchInfo(simpleMatchInfo: Schema$SimpleMatchTrack) {
    this.sportTalkPopupMatchTrack = simpleMatchInfo
  }
}

export default new CheerForTeamStore()
